import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import permissionService from '../../core/services/PermissionService';
import roleService from '../../core/services/RoleService';
import { refreshUserPermissions } from '../../core/store/actions/auth';
import { fetchPermissions, updatePermission } from '../../core/store/actions/permissions';
import { hasManageAccessControlPermission } from '../../core/utils/permissionUtils';
import { useDepartments } from '../../core/DepartmentsContext';
import { getDepartmentsForContext } from '../../core/constants/departments';
import DepartmentIcon from '../departments/DepartmentIcon';
import CustomAlert from '../CustomAlert';
import useUsers from '../hooks/useUsers';
// import CustomAlertWithButtons from '../CustomAlertWithButtons';
import CustomAlertWithButtons from '../CustomAlertWithButtons';
import './../../styles/PermissionManagement.css';
import LoadingSpinner from '../LoadingSpinner';
import NavHeader from "./../NavHeader";

const PERMISSION_TAG_ORDER = [
  'Access Control',
  'Events',
  'Open Calls',
  'Users',
  'Department',
  'Administration'
];

const UNCATEGORIZED_TAG = 'Uncategorized';

const getPermissionPrimaryTag = (permission) => permission.tags?.[0] || UNCATEGORIZED_TAG;

const sortTags = (tags) => {
  return [...tags].sort((a, b) => {
    const aIndex = PERMISSION_TAG_ORDER.indexOf(a);
    const bIndex = PERMISSION_TAG_ORDER.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
};

const getDepartmentTargetId = (department) =>
  String(department?._id || department?.name || '');

const normalizeDepartmentTargetIds = (departments = []) =>
  departments.map((department) =>
    typeof department === 'string' ? department : getDepartmentTargetId(department)
  ).filter(Boolean);

function PermissionManagement() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.profile);
  const permissionsState = useSelector((state) => state.permissions);
  const permissions = permissionsState.items || [];
  const [localPermissions, setLocalPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPermissionId, setExpandedPermissionId] = useState(null);
  const { userOptions, loading: usersLoading } = useUsers();
  const { departments, loading: departmentsLoading } = useDepartments();

  const assignableDepartments = useMemo(
    () => getDepartmentsForContext('registration', departments),
    [departments]
  );

  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: '',
    message: ''
  });

  const [alertActionInfo, setAlertActionInfo] = useState({
    show: false,
    title: '',
    type: '',
    message: '',
    btn1Action: () => { },
    btn2Action: () => { },
    btn1Text: '',
    btn2Text: ''
  });

  const [editingState, setEditingState] = useState({});

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const canManageAccessControl = hasManageAccessControlPermission(user);

  useEffect(() => {
    setLocalPermissions(Array.isArray(permissions) ? permissions : []);
  }, [permissions]);

  // Fetch all permissions and roles on component mount
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        // Check permission before making API calls
        if (!canManageAccessControl) {
          if (isMounted) {
            setAlertInfo({
              show: true,
              type: 'error',
              message: 'You do not have permission to access this page. Required permission: MANAGE_ACCESS_CONTROL'
            });
            setLoading(false);
          }
          return;
        }

        setLoading(true);
        const [permissionsResponse, rolesResponse] = await Promise.all([
          dispatch(fetchPermissions()),
          roleService.getAllRoles()
        ]);

        if (!isMounted) return;

        const rolesData = Array.isArray(rolesResponse) 
          ? rolesResponse 
          : (rolesResponse.data || []);

        const permissionsData = Array.isArray(permissionsResponse)
          ? permissionsResponse
          : (permissionsResponse?.data || []);

        setRoles(rolesData);
        
        // Initialize editing state
        const initialEditingState = {};
        permissionsData.forEach(perm => {
          initialEditingState[perm._id] = {
            roles: (perm.roles || []).map(r => typeof r === 'string' ? r : r._id),
            users: (perm.users || []).map(u => typeof u === 'string' ? u : u._id),
            tiers: perm.tiers || [],
            departments: normalizeDepartmentTargetIds(perm.departments)
          };
        });
        setEditingState(initialEditingState);
      } catch (error) {
        console.error('Error fetching data:', error);
        if (isMounted) {
          setAlertInfo({
            show: true,
            type: 'error',
            message: 'Error loading permissions and roles. Please try again.'
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [user?._id, canManageAccessControl, dispatch]);

  const togglePermissionExpanded = (permissionId) => {
    if (expandedPermissionId === permissionId) {
      setExpandedPermissionId(null);
    } else {
      setExpandedPermissionId(permissionId);
    }
  };

  const handleRoleToggle = (permissionId, roleId) => {
    setEditingState(prev => ({
      ...prev,
      [permissionId]: {
        ...prev[permissionId],
        roles: prev[permissionId].roles.includes(roleId)
          ? prev[permissionId].roles.filter(r => r !== roleId)
          : [...prev[permissionId].roles, roleId]
      }
    }));
  };

  const handleTierToggle = (permissionId, tier) => {
    setEditingState(prev => ({
      ...prev,
      [permissionId]: {
        ...prev[permissionId],
        tiers: prev[permissionId].tiers.includes(tier)
          ? prev[permissionId].tiers.filter(t => t !== tier)
          : [...prev[permissionId].tiers, tier]
      }
    }));
  };

  const handleDepartmentToggle = (permissionId, departmentId) => {
    setEditingState(prev => ({
      ...prev,
      [permissionId]: {
        ...prev[permissionId],
        departments: prev[permissionId].departments.includes(departmentId)
          ? prev[permissionId].departments.filter((id) => id !== departmentId)
          : [...prev[permissionId].departments, departmentId]
      }
    }));
  };

  const handleUserSelectChange = (permissionId, selectedOptions) => {
    const selectedUsers = (selectedOptions || []).map(option => option.value);
    setEditingState(prev => ({
      ...prev,
      [permissionId]: {
        ...prev[permissionId],
        users: selectedUsers
      }
    }));
  };

  const userOptionsById = useMemo(() => {
    return userOptions.reduce((acc, option) => {
      acc[option.value] = option;
      return acc;
    }, {});
  }, [userOptions]);

  useEffect(() => {
    if (localPermissions.length === 0 || expandedPermissionId !== null) {
      return;
    }

    const nextEditingState = {};
    localPermissions.forEach((perm) => {
      nextEditingState[perm._id] = {
        roles: (perm.roles || []).map((r) => (typeof r === 'string' ? r : r._id)),
        users: (perm.users || []).map((u) => (typeof u === 'string' ? u : u._id)),
        tiers: perm.tiers || [],
        departments: normalizeDepartmentTargetIds(perm.departments)
      };
    });
    setEditingState(nextEditingState);
  }, [localPermissions, expandedPermissionId]);

  const handleSavePermission = (permissionId) => {
    const permission = localPermissions.find((p) => p._id === permissionId);
    if (!permission) return;

    setAlertActionInfo({
      show: true,
      title: `Save Changes for "${permission.action_key}"`,
      type: 'warning',
      message: `Are you sure you want to save these permission changes?`,
      btn2Action: async () => {
        try {
          const updates = editingState[permissionId];
          const updatedPermission = await permissionService.updatePermissionTargets(
            permissionId,
            {
              roles: updates.roles,
              users: updates.users,
              tiers: updates.tiers,
              departments: updates.departments
            }
          );

          const normalizedPermission = updatedPermission?.data || updatedPermission;
          const mergedPermission = {
            ...permission,
            ...normalizedPermission
            // roles: normalizedPermission?.roles !== undefined ? normalizedPermission.roles : updates.roles,
            // users: normalizedPermission?.users !== undefined ? normalizedPermission.users : updates.users,
            // tiers: normalizedPermission?.tiers !== undefined ? normalizedPermission.tiers : updates.tiers
          };

          setLocalPermissions((prev) =>
            prev.map((item) => (item._id === permissionId ? mergedPermission : item))
          );
          setEditingState((prev) => ({
            ...prev,
            [permissionId]: {
              roles: (mergedPermission.roles || []).map((r) => (typeof r === 'string' ? r : r._id)),
              users: (mergedPermission.users || []).map((u) => (typeof u === 'string' ? u : u._id)),
              tiers: mergedPermission.tiers || [],
              departments: normalizeDepartmentTargetIds(mergedPermission.departments)
            }
          }));

          dispatch(updatePermission(mergedPermission));
          dispatch(refreshUserPermissions());

          setAlertInfo({
            show: true,
            type: 'success',
            message: 'Permission targets updated successfully.'
          });

          setExpandedPermissionId(null);
        } catch (error) {
          console.error('Error updating permission:', error);
          setAlertInfo({
            show: true,
            type: 'error',
            message: error.message || 'Error updating permission. Please try again.'
          });
        } finally {
          setAlertActionInfo((prevState) => ({
            ...prevState,
            show: false,
          }));
        }
      },
      btn1Action: () => {
        setAlertActionInfo((prevState) => ({
          ...prevState,
          show: false,
        }));
      },
      btn1Text: 'Cancel',
      btn2Text: 'Yes, save changes',
    });
  };

  const handleCancel = (permissionId) => {
    // Reset editing state to current values
    const permission = localPermissions.find(p => p._id === permissionId);
    if (permission) {
      setEditingState(prev => ({
        ...prev,
        [permissionId]: {
          roles: (permission.roles || []).map(r => typeof r === 'string' ? r : r._id),
          users: (permission.users || []).map(u => typeof u === 'string' ? u : u._id),
          tiers: permission.tiers || [],
          departments: normalizeDepartmentTargetIds(permission.departments)
        }
      }));
    }
    setExpandedPermissionId(null);
  };

  const membershipTiers = ['Newbie', 'Junior', 'Senior'];

  const hasTargets = (permission) => {
    return (permission.roles && permission.roles.length > 0) ||
           (permission.users && permission.users.length > 0) ||
           (permission.tiers && permission.tiers.length > 0) ||
           (permission.departments && permission.departments.length > 0);
  };

  const getTargetCount = (permission) =>
    (permission.roles?.length || 0) +
    (permission.users?.length || 0) +
    (permission.tiers?.length || 0) +
    (permission.departments?.length || 0);

  const availableTags = useMemo(() => {
    const tagSet = new Set();
    localPermissions.forEach((permission) => {
      if (permission.tags?.length) {
        permission.tags.forEach((tag) => tagSet.add(tag));
      } else {
        tagSet.add(UNCATEGORIZED_TAG);
      }
    });
    return sortTags(Array.from(tagSet));
  }, [localPermissions]);

  const getFilteredPermissions = useCallback(() => {
    const lowerCaseQuery = searchQuery.trim().toLowerCase();

    return localPermissions.filter((permission) => {
      const matchesTag =
        !selectedTag ||
        (permission.tags || []).includes(selectedTag) ||
        (selectedTag === UNCATEGORIZED_TAG && (!permission.tags || permission.tags.length === 0));

      if (!matchesTag) {
        return false;
      }

      if (!lowerCaseQuery) {
        return true;
      }

      return (
        permission.action_key.toLowerCase().includes(lowerCaseQuery) ||
        (permission.description || '').toLowerCase().includes(lowerCaseQuery) ||
        (permission.tags || []).some((tag) => tag.toLowerCase().includes(lowerCaseQuery))
      );
    });
  }, [localPermissions, searchQuery, selectedTag]);

  const groupedPermissions = useMemo(() => {
    const filtered = getFilteredPermissions();

    if (selectedTag) {
      return [{ tag: selectedTag, permissions: filtered }];
    }

    const groups = new Map();
    filtered.forEach((permission) => {
      const tag = getPermissionPrimaryTag(permission);
      if (!groups.has(tag)) {
        groups.set(tag, []);
      }
      groups.get(tag).push(permission);
    });

    return sortTags(Array.from(groups.keys())).map((tag) => ({
      tag,
      permissions: groups.get(tag).sort((a, b) => a.action_key.localeCompare(b.action_key))
    }));
  }, [getFilteredPermissions, selectedTag]);

  const filteredPermissionCount = useMemo(
    () => getFilteredPermissions().length,
    [getFilteredPermissions]
  );

  const renderPermissionCard = (permission) => (
    <div key={permission._id} className={`permission-card ${expandedPermissionId === permission._id ? 'expanded' : ''}`}>
      <div
        className="permission-header"
        onClick={() => togglePermissionExpanded(permission._id)}
      >
        <div className="permission-info">
          <h3 className="permission-key">{permission.action_key}</h3>
          <p className="permission-description">
            {permission.description || 'No description'}
          </p>
          {permission.tags?.length > 0 && (
            <div className="permission-tag-badges">
              {permission.tags.map((tag) => (
                <span key={tag} className="permission-tag-badge">{tag}</span>
              ))}
            </div>
          )}
        </div>
        <div className="permission-summary">
          {!hasTargets(permission) && (
            <span className="warning-badge">
              <Icon icon="f7:exclamationmark-circle-fill" className="warning-icon" />
              No targets configured
            </span>
          )}
          {hasTargets(permission) && (
            <span className="targets-count">
              <Icon icon="f7:checkmark-circle-fill" className="success-icon" />
              {getTargetCount(permission)} targets
            </span>
          )}
          <Icon
            icon={expandedPermissionId === permission._id ? "f7:chevron-up" : "f7:chevron-down"}
            className="expand-icon"
          />
        </div>
      </div>

      {expandedPermissionId === permission._id && (
        <div className="permission-details">
          <div className="permission-details-grid">
            <div className="targets-section">
              <h4 className="targets-section-title">
                <Icon icon="f7:person-circle-fill" className="section-icon" />
                Roles
              </h4>
              <div className="target-options-grid">
                {roles.length > 0 ? (
                  roles.map(role => {
                    const isSelected = editingState[permission._id]?.roles.includes(role._id) || false;
                    return (
                      <button
                        key={role._id}
                        type="button"
                        className={`target-option-card${isSelected ? ' selected' : ''}`}
                        aria-pressed={isSelected}
                        onClick={() => handleRoleToggle(permission._id, role._id)}
                      >
                        <span className="target-option-card-text">
                          {role.name}
                          {role.isProtected && (
                            <Icon icon="f7:lock-shield-fill" className="protected-badge" title="Protected role" />
                          )}
                        </span>
                      </button>
                    );
                  })
                ) : (
                  <p className="empty-targets">No roles available</p>
                )}
              </div>
            </div>

            <div className="targets-section">
              <h4 className="targets-section-title">
                <Icon icon="f7:person-3-fill" className="section-icon" />
                Membership Tiers
              </h4>
              <div className="target-options-grid target-options-grid--compact">
                {membershipTiers.map(tier => {
                  const isSelected = editingState[permission._id]?.tiers.includes(tier) || false;
                  return (
                    <button
                      key={tier}
                      type="button"
                      className={`target-option-card${isSelected ? ' selected' : ''}`}
                      aria-pressed={isSelected}
                      onClick={() => handleTierToggle(permission._id, tier)}
                    >
                      <span className="target-option-card-text">{tier}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="targets-section">
            <h4 className="targets-section-title">
              <Icon icon="f7:building-2-fill" className="section-icon" />
              Departments
            </h4>
            <small className="help-text">
              Grant this permission to all members of the selected departments for the current semester.
            </small>
            <div className="target-options-grid">
              {departmentsLoading ? (
                <p className="empty-targets">Loading departments...</p>
              ) : assignableDepartments.length > 0 ? (
                assignableDepartments.map((department) => {
                  const departmentTargetId = getDepartmentTargetId(department);
                  const isSelected = editingState[permission._id]?.departments.includes(departmentTargetId) || false;
                  return (
                    <button
                      key={departmentTargetId}
                      type="button"
                      className={`target-option-card${isSelected ? ' selected' : ''}`}
                      aria-pressed={isSelected}
                      onClick={() => handleDepartmentToggle(permission._id, departmentTargetId)}
                    >
                      <span className="target-option-card-text">
                        <DepartmentIcon department={department} style={{ fontSize: '0.9rem' }} />
                        {department.name}
                      </span>
                    </button>
                  );
                })
              ) : (
                <p className="empty-targets">No departments available</p>
              )}
            </div>
          </div>

          <div className="targets-section targets-section--users">
            <h4 className="targets-section-title">
              <Icon icon="f7:person-fill" className="section-icon" />
              User Overrides
            </h4>
            <small className="help-text">
              Select specific users to grant this permission directly.
            </small>
            <Select
              isMulti
              className="user-select"
              classNamePrefix="user-select"
              isDisabled={usersLoading}
              placeholder={usersLoading ? 'Loading users...' : 'Search and select users'}
              options={userOptions}
              value={(editingState[permission._id]?.users || []).map((userId) => (
                userOptionsById[userId] || { value: userId, label: userId }
              ))}
              onChange={(selectedOptions) => handleUserSelectChange(permission._id, selectedOptions)}
            />
          </div>

          <div className="permission-actions">
            <Button
              variant="primary"
              onClick={() => handleSavePermission(permission._id)}
              className="btn-save"
            >
              <Icon icon="f7:checkmark-circle-fill" className="btn-icon" />
              Save Changes
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleCancel(permission._id)}
              className="btn-cancel"
            >
              <Icon icon="f7:xmark-circle-fill" className="btn-icon" />
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Calculate how many permissions have no targets
  const permissionsWithoutTargets = localPermissions.filter(p => !hasTargets(p));
  const hasUnconfiguredPermissions = permissionsWithoutTargets.length > 0;

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className='page-container'>
      <NavHeader />
      <div className="permission-management-container">
        
        {alertInfo.show && (
          <CustomAlert
            type={alertInfo.type}
            message={alertInfo.message}
            onClose={() => setAlertInfo({ ...alertInfo, show: false })}
          />
        )}

        {alertActionInfo.show && (
          <CustomAlertWithButtons
            title={alertActionInfo.title}
            type={alertActionInfo.type}
            message={alertActionInfo.message}
            button1Text={alertActionInfo.btn1Text}
            button2Text={alertActionInfo.btn2Text}
            onButton1Click={alertActionInfo.btn1Action}
            onButton2Click={alertActionInfo.btn2Action}
          />
        )}
        
        <div className="permission-management-header">
          <h1 className="page-title">Permission Management</h1>
        </div>

        <div className="main-info-banner">
          <Icon
            icon="f7:info-circle-fill"
            className="info-banner-icon"
          />
          <div>
            <strong>Missing a permission?</strong>
            <p>
              If you cannot find a permission you need, please{' '}
              <a
                href="https://forms.gle/CqJL4dtpGF1EzvxH9"
                target="_blank"
                rel="noopener noreferrer"
              >
                fill out this Google Form
              </a>.
            </p>
          </div>
        </div>

        {/* Main Warning for Unconfigured Permissions */}
        {hasUnconfiguredPermissions && (
          <div className="main-warning-banner" style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icon icon="f7:exclamationmark-triangle-fill" style={{
              color: '#ff9800',
              fontSize: '24px',
              flexShrink: 0
            }} />
            <div>
              <strong>⚠️ {permissionsWithoutTargets.length} permission(s) have no allowed targets configured</strong>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                These permissions are currently inaccessible to all users. Please assign at least one target (role, tier, department, or user) to each permission.
              </p>
            </div>
          </div>
        )}

        {/* Search Bar Section */}
        <div className="permission-search-section">
          <div className="search-input-wrapper">
            <Icon icon="f7:magnifyingglass" className="search-icon" />
            <input
              type="text"
              className="permission-search-input"
              placeholder="Search permissions by name, description, or tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="search-clear-button"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                <Icon icon="f7:xmark-circle-fill" />
              </button>
            )}
          </div>
        </div>

        {/* Tag Filter Section */}
        {availableTags.length > 0 && (
          <div className="permission-tag-filter-section">
            <div className="permission-tag-filters">
              <button
                type="button"
                className={`permission-tag-filter${selectedTag === null ? ' selected' : ''}`}
                aria-pressed={selectedTag === null}
                onClick={() => setSelectedTag(null)}
              >
                All
                <span className="permission-tag-filter-count">{localPermissions.length}</span>
              </button>
              {availableTags.map((tag) => {
                const tagCount = localPermissions.filter((permission) =>
                  tag === UNCATEGORIZED_TAG
                    ? !permission.tags || permission.tags.length === 0
                    : (permission.tags || []).includes(tag)
                ).length;

                return (
                  <button
                    key={tag}
                    type="button"
                    className={`permission-tag-filter${selectedTag === tag ? ' selected' : ''}`}
                    aria-pressed={selectedTag === tag}
                    onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                  >
                    {tag}
                    <span className="permission-tag-filter-count">{tagCount}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Permissions List Section */}
        <div className="permissions-list-section">
          <div className="section-title">
            <Icon icon="f7:lock-circle-fill" className="section-icon" />
            System Permissions ({filteredPermissionCount})
          </div>

          {localPermissions.length === 0 ? (
            <div className="empty-state">
              <Icon icon="f7:circle-slash" className="empty-icon" />
              <p>No permissions found.</p>
            </div>
          ) : filteredPermissionCount === 0 ? (
            <div className="empty-state">
              <Icon icon="f7:circle-slash" className="empty-icon" />
              <p>No permissions match your search or filter. Try different keywords or categories.</p>
            </div>
          ) : (
            <div className="permission-groups">
              {groupedPermissions.map(({ tag, permissions }) => (
                <div key={tag} className="permission-tag-group">
                  {!selectedTag && (
                    <h3 className="permission-tag-group-title">
                      <Icon icon="f7:folder-fill" className="permission-tag-group-icon" />
                      {tag}
                      <span className="permission-tag-group-count">{permissions.length}</span>
                    </h3>
                  )}
                  <div className="permissions-list">
                    {permissions.map((permission) => renderPermissionCard(permission))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PermissionManagement;
